import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const SHOW_DELAY = 1000; // 1 segundo

const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TooltipContent = styled.div`
  position: fixed;
  z-index: 10100; /* acima do overlay do tour (10050) */
  padding: 7px 12px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme?.body === '#fafbfc'
      ? 'rgba(30, 30, 35, 0.95)'
      : 'rgba(255, 255, 255, 0.95)'};
  color: ${({ theme }) =>
    theme?.body === '#fafbfc'
      ? '#ffffff'
      : '#1a1d24'};
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(8px);
  border: 1px solid ${({ theme }) =>
    theme?.body === '#fafbfc'
      ? 'rgba(255,255,255,0.1)'
      : 'rgba(0,0,0,0.1)'};

  &::after {
    content: '';
    position: absolute;
    border: 5px solid transparent;
  }

  &[data-visible='true'] {
    opacity: 1;
    transform: translateY(0);
  }
`;

function computePosition(rect, placement) {
  const GAP = 8;
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2;
      break;
    case 'top':
      top = rect.top - GAP;
      left = rect.left + rect.width / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2;
      left = rect.right + GAP;
      break;
    case 'left':
      top = rect.top + rect.height / 2;
      left = rect.left - GAP;
      break;
    default:
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2;
  }

  return { top, left };
}

export default function Tooltip({ children, content, placement = 'top' }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  const measure = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPos(computePosition(rect, placement));
  }, [placement]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    measure();
    timeoutRef.current = setTimeout(() => setVisible(true), SHOW_DELAY);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setVisible(false);
  };

  const transform = placement === 'bottom' || placement === 'top'
    ? 'translate(-50%, 0)'
    : 'translate(0, -50%)';

  return (
    <TooltipContainer
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {createPortal(
        <TooltipContent
          style={{ top: pos.top, left: pos.left, transform }}
          data-visible={visible}
          data-placement={placement}
        >
          {content}
        </TooltipContent>,
        document.body
      )}
    </TooltipContainer>
  );
}
